/**
 * طبقة الاتصال بالتوظيف — `GET /hiring/summary` · `/applicants` ·
 * `POST /applicants` · `.../move` · `.../convert`.
 *
 * 🔴 **الفجوة:** المتقدمون في شيت، والقمع في دماغ مسؤولة التوظيف.
 *    الشيت ما بيقولش «مين مستني مقابلة من إمتى» — فالمتقدم بيضيع بين
 *    الفلترة والمقابلة ومحدش بيلاحظ.
 *
 * ⚠️ **`null` مش صفر**: `formApplications = null` معناها «نموذج التقديم
 *    مش موجود»، والصفر معناه «مفيش متقدمين» — والخلط بيخلّي الشاشة تكدب.
 */
import { ApiError, api } from "./client";
import { latinDigits } from "./registry";

type Raw = Record<string, unknown>;

function fail(path: string, reason: string): never {
  console.error(`[hiring] ${reason} (${path})`);
  throw new ApiError(502, { code: "EMPTY_RESPONSE",
                            message_ar: "السيستم رجّع رد مش مفهوم" }, null);
}
function str(v: unknown): string {
  return typeof v === "string" ? latinDigits(v) : "";
}
function strOrNull(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? latinDigits(v) : null;
}
function num(v: unknown, d = 0): number {
  return typeof v === "number" && Number.isFinite(v) ? v : d;
}
function numOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function obj(v: unknown): Raw {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Raw) : {};
}

export const STAGE_AR: Record<string, string> = {
  new: "جديد", screening: "الفلترة", interview: "المقابلة",
  training: "التدريب", hired: "اتعيّن", rejected: "مرفوض",
};
export const STAGE_ORDER = ["new", "screening", "interview", "training",
                            "hired", "rejected"] as const;

export type ApplicantRow = {
  id: string;
  name_ar: string;
  phone_e164: string | null;
  email: string | null;
  qualification: string | null;
  has_ijazah_certificate: boolean;
  language_level: string | null;
  online_experience_years: number;
  auto_filter_ar: string;
  status: string;
  status_ar: string;
  /** المراحل المسموح الانتقال لها — **جاية من السيرفر**، الشاشة ما بتخترعهاش */
  next_stages: string[];
  batch_id: string | null;
  teacher_id: string | null;
  rejection_reason_ar: string | null;
  waiting_since: string | null;
  notes_ar: string | null;
  version: number;
};

function row(v: unknown): ApplicantRow {
  const r = obj(v);
  return {
    id: str(r["id"]), name_ar: str(r["name_ar"]),
    phone_e164: strOrNull(r["phone_e164"]), email: strOrNull(r["email"]),
    qualification: strOrNull(r["qualification"]),
    has_ijazah_certificate: r["has_ijazah_certificate"] === true,
    language_level: strOrNull(r["language_level"]),
    online_experience_years: num(r["online_experience_years"]),
    auto_filter_ar: str(r["auto_filter_ar"]),
    status: str(r["status"]), status_ar: str(r["status_ar"]),
    next_stages: Array.isArray(r["next_stages"])
      ? (r["next_stages"] as unknown[]).map(str).filter(Boolean) : [],
    batch_id: strOrNull(r["batch_id"]),
    teacher_id: strOrNull(r["teacher_id"]),
    rejection_reason_ar: strOrNull(r["rejection_reason_ar"]),
    waiting_since: strOrNull(r["waiting_since"]),
    notes_ar: strOrNull(r["notes_ar"]),
    version: num(r["version"], 1),
  };
}

export type ApplicantsMeta = {
  page: number; perPage: number; total: number; totalPages: number;
  stageCounts: Record<string, number>;
};

export async function listApplicants(
  opts: { status?: string; q?: string; page?: number; perPage?: number } = {},
): Promise<{ rows: ApplicantRow[]; meta: ApplicantsMeta }> {
  const path = "/applicants";
  const res = await api.get<unknown>(path, {
    query: { status: opts.status || undefined, q: opts.q || undefined,
             page: opts.page ?? 1, per_page: opts.perPage ?? 25 },
  });
  if (!Array.isArray(res.data)) fail(path, "قايمة المتقدمين رجعت بشكل مش متوقع");
  const m = obj(res.meta);
  const sc = obj(m["stage_counts"]);
  return {
    rows: (res.data as unknown[]).map(row),
    meta: {
      page: num(m["page"], 1), perPage: num(m["per_page"], 25),
      total: num(m["total"]), totalPages: num(m["total_pages"], 1),
      stageCounts: Object.fromEntries(Object.entries(sc).map(([k, v]) => [k, num(v)])),
    },
  };
}

export async function createApplicant(body: {
  name_ar: string; phone_e164?: string; email?: string; qualification?: string;
  has_ijazah_certificate?: boolean; language_level?: string;
  online_experience_years?: number; batch_id?: string; notes_ar?: string;
}): Promise<{ row: ApplicantRow; toastAr: string }> {
  const res = await api.post<unknown>("/applicants", body);
  const d = obj(res.data);
  return { row: row(d), toastAr: str(d["toast_ar"]) || "اتسجّل" };
}

/** ⛔ الانتقال بالسلسلة المقفولة بس — السيرفر بيرفض القفز */
export async function moveApplicant(
  id: string, to: string, version: number, reasonAr?: string,
): Promise<{ row: ApplicantRow; toastAr: string }> {
  const res = await api.post<unknown>(`/applicants/${id}/move`,
                                      { to, reason_ar: reasonAr },
                                      { ifMatch: version });
  const d = obj(res.data);
  return { row: row(d), toastAr: str(d["toast_ar"]) || "اتنقل" };
}

export async function convertApplicant(
  id: string, version: number,
): Promise<{ row: ApplicantRow; toastAr: string }> {
  const res = await api.post<unknown>(`/applicants/${id}/convert`, undefined,
                                      { ifMatch: version });
  const d = obj(res.data);
  return { row: row(d), toastAr: str(d["toast_ar"]) || "اتحوّل" };
}

export type HiringSummary = {
  stageCounts: Record<string, number>;
  total: number;
  inPipeline: number;
  /** `null` = نموذج التقديم مش موجود — **مش** صفر */
  formApplications: number | null;
};

export async function getHiringSummary(): Promise<HiringSummary> {
  const path = "/hiring/summary";
  const res = await api.get<unknown>(path);
  if (!res.data || typeof res.data !== "object") fail(path, "الملخّص رجع فاضي");
  const d = obj(res.data);
  const sc = obj(d["stage_counts"]);
  return {
    stageCounts: Object.fromEntries(Object.entries(sc).map(([k, v]) => [k, num(v)])),
    total: num(d["total"]),
    inPipeline: num(d["in_pipeline"]),
    formApplications: numOrNull(d["form_applications"]),
  };
}
